import { useState } from 'react';
import {
  Check, CircleNotch, Copy, Image as ImagenIcono, Link, Printer, ShareNetwork,
  UsersThree, Warning, X,
} from '@phosphor-icons/react';

// F-7 de ARQ-007: «Compartir» del menú de Procore, más «Publicar», que en su
// app es una acción de la galería de fotos enlazadas.
//
// LAS DOS MITADES NO SON LO MISMO Y POR ESO ESTÁN SEPARADAS:
//
//   COMPARTIR  saca el plano de acá: un enlace, un PNG, papel. No cambia
//              ningún dato — se puede apretar sin miedo.
//   PUBLICAR   SÍ cambia datos: pasa marcas personales a la capa del equipo, y
//              eso lo ve todo el mundo. Va al final, con su confirmación y
//              diciendo cuántas son.
//
// El mecanismo (armar el enlace, componer el canvas, abrir la ventana de
// impresión) vive entero en `compartir.ts`. Acá solo está la pantalla.

interface Props {
  /** El enlace ya armado a la vista actual. */
  url: string;
  /**
   * ¿Las tiles se dejan leer por código? `null` mientras se mide. Decide si la
   * instantánea PNG se puede ofrecer: sin CORS el canvas queda contaminado y
   * `toBlob` tira SecurityError. Imprimir no depende de esto.
   */
  corsTiles: boolean | null;
  /** Cuántas marcas personales hay en esta hoja. */
  personales: number;
  /** Se está comparando contra otra hoja: ni el PNG ni el papel llevan la mezcla. */
  comparando?: boolean;
  /** Hay PUTs de publicación en vuelo. */
  publicando?: boolean;
  onDescargarPng: () => Promise<void>;
  onCopiarPng: () => Promise<void>;
  onImprimir: () => Promise<void>;
  /** Sin esto la sección de publicar no aparece (el visor es de solo lectura). */
  onPublicarPersonales?: () => void;
  onCerrar: () => void;
}

export function PanelCompartir({
  url, corsTiles, personales, comparando, publicando,
  onDescargarPng, onCopiarPng, onImprimir, onPublicarPersonales, onCerrar,
}: Props) {
  // Un solo par ocupado/mensaje para las tres acciones: nunca hay dos
  // corriendo a la vez y tres pares de estado solo darían más formas de
  // quedar desincronizados.
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<{ texto: string; mal: boolean } | null>(null);

  const correr = async (clave: string, fn: () => Promise<void>, bien: string) => {
    setOcupado(clave);
    setMensaje(null);
    try {
      await fn();
      setMensaje({ texto: bien, mal: false });
    } catch (e) {
      // El motivo se muestra tal cual. Un «no se pudo» pelado sobre algo que
      // falla por una regla de infraestructura manda a nadie a ningún lado.
      setMensaje({ texto: e instanceof Error ? e.message : 'no se pudo', mal: true });
    } finally {
      setOcupado(null);
    }
  };

  const copiarEnlace = () =>
    correr('enlace', async () => {
      await navigator.clipboard.writeText(url);
    }, 'Enlace copiado');

  const puedePng = corsTiles === true;

  return (
    // `/95` y no `/97` (ver PanelCapas), y alto tope con scroll propio: este
    // panel es el más largo de los tres y en una pantalla de portátil se salía
    // del visor por abajo, que es `overflow-hidden`.
    <div className="max-h-[75vh] w-72 overflow-y-auto rounded-lg border border-brand-gray-200 bg-white/95 p-2 shadow-lg">
      <div className="mb-1.5 flex items-center gap-1.5 px-1">
        <ShareNetwork size={14} weight="bold" className="text-brand-gray-500" />
        <p className="flex-1 text-xs font-semibold text-brand-gray-500">Compartir</p>
        <button type="button" onClick={onCerrar} aria-label="Cerrar compartir"
          className="rounded p-0.5 text-brand-gray-400 hover:bg-brand-gray-100">
          <X size={13} weight="bold" />
        </button>
      </div>

      {/* El modo comparación tiñe el plano con dos capas mezcladas en el
          navegador. Ni el canvas ni el papel reproducen ese `mix-blend-mode`,
          así que lo que sale es la hoja base sola. Decirlo acá evita que la
          foto o la impresión se lean como que la comparación se perdió. */}
      {comparando && (
        <div className="mb-1.5 flex gap-1.5 rounded border border-brand-gray-300 bg-brand-bg px-1.5 py-1">
          <Warning size={13} weight="bold" className="mt-0.5 shrink-0 text-brand-gray-400" />
          <p data-compartir-aviso-comparar className="text-[10px] leading-snug text-brand-gray-500">
            Estás comparando: la instantánea y el papel salen con la{' '}
            <strong>hoja base sola</strong>, sin la mezcla de colores. El enlace
            sí vuelve a esta vista, pero sin la comparación.
          </p>
        </div>
      )}

      {/* 1. El enlace. Va primero porque es lo que más se usa y lo único que
          reproduce el encuadre: «mirá ESTO» sin explicar dónde mirar. */}
      <Titulo icono={<Link size={11} weight="bold" />}>Enlace a esta vista</Titulo>
      <input
        readOnly
        data-compartir-url
        value={url}
        onFocus={ev => ev.currentTarget.select()}
        className="mb-1 w-full rounded border border-brand-gray-200 bg-brand-bg px-1.5 py-1 font-mono text-[10px] text-brand-gray-500"
      />
      <Accion
        clave="enlace" ocupado={ocupado} onClick={copiarEnlace}
        icono={<Copy size={13} weight="bold" />}
      >
        Copiar el enlace
      </Accion>
      <p className="mb-1.5 mt-1 px-1 text-[10px] leading-snug text-brand-gray-400">
        Abre esta lámina en este encuadre y con estas capas. El zoom viaja como
        el punto del plano que quedó al centro, así que se ve igual en otra
        pantalla.
      </p>

      <div className="my-1.5 h-px bg-brand-gray-200" />

      {/* 2. La instantánea. Es lo que se ve, recortado como está en pantalla. */}
      <Titulo icono={<ImagenIcono size={11} weight="bold" />}>Instantánea de lo que ves</Titulo>
      {corsTiles === null ? (
        <p className="px-1 py-1 text-[11px] text-brand-gray-400">Midiendo si se puede…</p>
      ) : puedePng ? (
        <>
          <Accion clave="png" ocupado={ocupado}
            onClick={() => correr('png', onDescargarPng, 'PNG descargado')}
            icono={<ImagenIcono size={13} weight="bold" />}>
            Descargar PNG
          </Accion>
          {/* Copiar al portapapeles es el reemplazo honesto de la «instantánea
              por correo» de Procore: un `mailto:` no adjunta archivos, pero la
              imagen pegada en el correo llega igual. */}
          {typeof window !== 'undefined' && 'ClipboardItem' in window && (
            <Accion clave="copiarPng" ocupado={ocupado}
              onClick={() => correr('copiarPng', onCopiarPng, 'Imagen copiada: pegala en el correo')}
              icono={<Copy size={13} weight="bold" />}>
              Copiar la imagen
            </Accion>
          )}
        </>
      ) : (
        // No es un botón deshabilitado y mudo: dice por qué y qué hacer en su
        // lugar. El arreglo es una regla de CORS en el almacenamiento, no algo
        // que se resuelva volviendo a apretar.
        <div className="mb-1 flex gap-1.5 rounded border border-brand-yellow bg-brand-yellow/10 px-1.5 py-1">
          <Warning size={13} weight="bold" className="mt-0.5 shrink-0 text-brand-gray-500" />
          <p className="text-[10px] leading-snug text-brand-gray-500">
            El almacenamiento de las tiles no permite leerlas por código (falta
            la regla de CORS), así que el PNG no se puede armar en el navegador.
            <strong> Imprimir sí funciona</strong>: ahí las pinta el navegador.
          </p>
        </div>
      )}

      <div className="my-1.5 h-px bg-brand-gray-200" />

      {/* 3. Papel. Imprime la HOJA COMPLETA, no el encuadre: en papel nadie
          quiere el recorte con el que uno estaba mirando un detalle. */}
      <Titulo icono={<Printer size={11} weight="bold" />}>Imprimir</Titulo>
      <Accion clave="imprimir" ocupado={ocupado}
        onClick={() => correr('imprimir', onImprimir, 'Se abrió la ventana de impresión')}
        icono={<Printer size={13} weight="bold" />}>
        Imprimir la hoja completa
      </Accion>
      <p className="mb-1.5 mt-1 px-1 text-[10px] leading-snug text-brand-gray-400">
        Sale la hoja entera con las marcas visibles, en una ventana aparte.
        Elegí «Guardar como PDF» para mandarla por correo.
      </p>

      {mensaje && (
        <p className={`mb-1 flex items-start gap-1 px-1 text-[10px] leading-snug ${
          mensaje.mal ? 'text-brand-red-200' : 'text-brand-gray-500'
        }`}>
          {!mensaje.mal && <Check size={11} weight="bold" className="mt-0.5 shrink-0" />}
          <span data-compartir-mensaje>{mensaje.texto}</span>
        </p>
      )}

      {onPublicarPersonales && (
        <>
          <div className="my-1.5 h-px bg-brand-gray-200" />
          <Titulo icono={<UsersThree size={11} weight="bold" />}>Publicar</Titulo>
          {personales === 0 ? (
            <p className="px-1 py-1 text-[11px] leading-snug text-brand-gray-400">
              No tenés marcas personales en esta hoja. Las que dibujes en la
              capa <strong>personal</strong> aparecen acá para publicarlas de
              una.
            </p>
          ) : (
            <>
              <button
                type="button"
                data-compartir="publicar"
                disabled={!!publicando}
                onClick={() => {
                  // Publicar es irreversible desde la vereda del otro: una vez
                  // que el equipo la vio, despublicarla no la borra de la
                  // cabeza de nadie. Por eso pregunta, y dice cuántas.
                  const va = window.confirm(
                    `¿Publicar ${personales} marca${personales === 1 ? '' : 's'} personal${
                      personales === 1 ? '' : 'es'} de esta hoja? Las va a ver todo el equipo.`,
                  );
                  if (va) onPublicarPersonales();
                }}
                className="mb-1 flex w-full items-center gap-1.5 rounded bg-brand-green-100 px-2 py-1.5 text-xs font-semibold text-black hover:bg-brand-green-200 disabled:opacity-60"
              >
                {publicando
                  ? <CircleNotch size={13} weight="bold" className="animate-spin" />
                  : <UsersThree size={13} weight="bold" />}
                Publicar {personales === 1 ? 'mi marca personal' : `mis ${personales} marcas personales`}
              </button>
              <p className="px-1 text-[10px] leading-snug text-brand-gray-400">
                Pasan a la capa del equipo una por una. Para publicar solo una,
                elegila en el plano y usá el selector de capa de la barra.
              </p>
            </>
          )}
        </>
      )}

      <div className="my-1.5 h-px bg-brand-gray-200" />
      {/* Las dos funciones medidas en Procore que acá NO existen. Decirlo es
          parte del trabajo: sin esto, buscarlas y no encontrarlas se lee como
          que la pantalla está incompleta. */}
      <p className="px-1 text-[10px] leading-snug text-brand-gray-400">
        Las <strong>fotos</strong>, los chinches y los vínculos no tienen capa
        personal: son siempre del equipo.
        {' '}<strong>«Remover del dispositivo»</strong> no aplica — la web no se
        guarda el plano, cada hoja se pide al abrirla.
      </p>
    </div>
  );
}

function Titulo({ icono, children }: { icono: React.ReactNode; children: React.ReactNode }) {
  return (
    <p className="mb-0.5 flex items-center gap-1 px-1 text-[10px] font-bold uppercase tracking-wide text-brand-gray-400">
      {icono}{children}
    </p>
  );
}

/** Un botón de acción con su propio spinner. Las tres comparten la forma. */
function Accion({ clave, ocupado, onClick, icono, children }: {
  clave: string;
  ocupado: string | null;
  onClick: () => void;
  icono: React.ReactNode;
  children: React.ReactNode;
}) {
  const corriendo = ocupado === clave;
  return (
    <button
      type="button"
      data-compartir={clave}
      // Deshabilitado mientras CUALQUIERA de las tres corre: imprimir mientras
      // se compone el PNG deja dos trabajos pidiendo las mismas tiles.
      disabled={ocupado !== null}
      onClick={onClick}
      className="mb-1 flex w-full items-center gap-1.5 rounded border border-brand-gray-300 px-2 py-1.5 text-xs font-semibold text-brand-gray-500 hover:bg-brand-gray-100 disabled:opacity-50"
    >
      {corriendo
        ? <CircleNotch size={13} weight="bold" className="animate-spin" />
        : icono}
      {children}
    </button>
  );
}
